import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type {
  KeyboardEvent as ReactKeyboardEvent,
  PointerEvent as ReactPointerEvent,
  WheelEvent as ReactWheelEvent,
} from "react";
import { getSurfaceSize, surfaceToUv, uvToSurface } from "../../skin/uv";
import type {
  RgbaHex as SkinColor,
  SkinFace,
  SkinLayer,
  SkinOperation,
  SkinPart,
  SurfaceRef,
} from "../../skin/types";
import { useSkinStore } from "../../store/skinStore";
import { Icon, type IconName } from "../ui/Icon";
import { cx } from "../../styles/cx";
import panel from "../../styles/panel.module.css";
import styles from "./EditorPanel.module.css";

const Part3DEditor = lazy(() => import("./Part3DEditor"));

const PARTS: Array<{ value: SkinPart; label: string }> = [
  { value: "head", label: "头部" },
  { value: "body", label: "身体" },
  { value: "rightArm", label: "右臂" },
  { value: "leftArm", label: "左臂" },
  { value: "rightLeg", label: "右腿" },
  { value: "leftLeg", label: "左腿" },
];

const FACES: Array<{ value: SkinFace; label: string }> = [
  { value: "front", label: "正面" },
  { value: "right", label: "右侧" },
  { value: "back", label: "背面" },
  { value: "left", label: "左侧" },
  { value: "top", label: "顶部" },
  { value: "bottom", label: "底部" },
];

type EditorTool =
  | "pencil"
  | "eraser"
  | "eyedropper"
  | "fill"
  | "line"
  | "rectangle";

const TOOLS: Array<{
  value: EditorTool;
  label: string;
  icon: IconName;
  key: string;
}> = [
  { value: "pencil", label: "铅笔", icon: "pencil", key: "1" },
  { value: "eraser", label: "橡皮", icon: "eraser", key: "2" },
  { value: "eyedropper", label: "吸管", icon: "eyedropper", key: "3" },
  { value: "fill", label: "油漆桶", icon: "fill", key: "4" },
  { value: "line", label: "直线", icon: "line", key: "L" },
  { value: "rectangle", label: "矩形", icon: "rectangle", key: "R" },
];

interface LocalPoint {
  x: number;
  y: number;
}

function colorAt(
  pixels: Uint8ClampedArray,
  x: number,
  y: number,
  size: 64 | 128,
): SkinColor {
  const offset = (y * size + x) * 4;
  return `#${[
    pixels[offset],
    pixels[offset + 1],
    pixels[offset + 2],
    pixels[offset + 3],
  ]
    .map((channel) => channel.toString(16).padStart(2, "0"))
    .join("")}`.toUpperCase() as SkinColor;
}

function colorToRgba(color: SkinColor, opacity = 1) {
  const r = Number.parseInt(color.slice(1, 3), 16);
  const g = Number.parseInt(color.slice(3, 5), 16);
  const b = Number.parseInt(color.slice(5, 7), 16);
  const a = (Number.parseInt(color.slice(7, 9), 16) / 255) * opacity;
  return `rgba(${r}, ${g}, ${b}, ${a})`;
}

function withAlpha(color: SkinColor, alpha: number): SkinColor {
  return `${color.slice(0, 7)}${Math.round(alpha).toString(16).padStart(2, "0")}`.toUpperCase() as SkinColor;
}

function isSkinColor(value: string): value is SkinColor {
  return /^#[\dA-Fa-f]{8}$/.test(value);
}

function bresenham(from: LocalPoint, to: LocalPoint): LocalPoint[] {
  const points: LocalPoint[] = [];
  let x = from.x;
  let y = from.y;
  const dx = Math.abs(to.x - from.x);
  const sx = from.x < to.x ? 1 : -1;
  const dy = -Math.abs(to.y - from.y);
  const sy = from.y < to.y ? 1 : -1;
  let error = dx + dy;

  while (true) {
    points.push({ x, y });
    if (x === to.x && y === to.y) break;
    const twice = 2 * error;
    if (twice >= dy) {
      error += dy;
      x += sx;
    }
    if (twice <= dx) {
      error += dx;
      y += sy;
    }
  }
  return points;
}

function rectangleOutline(
  from: LocalPoint,
  to: LocalPoint,
  filled: boolean,
): LocalPoint[] {
  const left = Math.min(from.x, to.x);
  const right = Math.max(from.x, to.x);
  const top = Math.min(from.y, to.y);
  const bottom = Math.max(from.y, to.y);
  const points: LocalPoint[] = [];
  for (let y = top; y <= bottom; y += 1) {
    for (let x = left; x <= right; x += 1) {
      if (filled || x === left || x === right || y === top || y === bottom)
        points.push({ x, y });
    }
  }
  return points;
}

function checkerColor(x: number, y: number) {
  return (x + y) % 2 === 0 ? "#f6f1e5" : "#ded6c4";
}

function surfaceLabel(part: SkinPart) {
  return PARTS.find((item) => item.value === part)?.label ?? part;
}

export function EditorPanel({
  mobileActive = false,
}: {
  mobileActive?: boolean;
}) {
  const document = useSkinStore((state) => state.document);
  const selectedSurface = useSkinStore((state) => state.selectedSurface);
  const editorMode = useSkinStore((state) => state.editorMode);
  const tool = useSkinStore((state) => state.tool);
  const color = useSkinStore((state) => state.color);
  const brushSize = useSkinStore((state) => state.brushSize);
  const activeLayer = useSkinStore((state) => state.activeLayer);
  const showInner = useSkinStore((state) => state.showInner);
  const showOuter = useSkinStore((state) => state.showOuter);
  const mirror = useSkinStore((state) => state.mirror);
  const zoom = useSkinStore((state) => state.zoom);
  const isAiRunning = useSkinStore((state) => state.isAiRunning);
  const history = useSkinStore((state) => state.history);
  const future = useSkinStore((state) => state.future);
  const recentColors = useSkinStore((state) => state.recentColors);
  const setSelectedSurface = useSkinStore((state) => state.setSelectedSurface);
  const setEditorMode = useSkinStore((state) => state.setEditorMode);
  const setTool = useSkinStore((state) => state.setTool);
  const setColor = useSkinStore((state) => state.setColor);
  const setBrushSize = useSkinStore((state) => state.setBrushSize);
  const setActiveLayer = useSkinStore((state) => state.setActiveLayer);
  const toggleLayer = useSkinStore((state) => state.toggleLayer);
  const setMirror = useSkinStore((state) => state.setMirror);
  const setZoom = useSkinStore((state) => state.setZoom);
  const applyOperations = useSkinStore((state) => state.applyOperations);
  const beginStroke = useSkinStore((state) => state.beginStroke);
  const commitStroke = useSkinStore((state) => state.commitStroke);
  const undo = useSkinStore((state) => state.undo);
  const redo = useSkinStore((state) => state.redo);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const drawingRef = useRef(false);
  const panningRef = useRef(false);
  const startRef = useRef<LocalPoint | null>(null);
  const lastRef = useRef<LocalPoint | null>(null);
  const panStartRef = useRef({ pointerX: 0, pointerY: 0, panX: 0, panY: 0 });
  const [coordinate, setCoordinate] = useState<LocalPoint | null>(null);
  const [keyboardCursor, setKeyboardCursor] = useState<LocalPoint>({
    x: 0,
    y: 0,
  });
  const [keyboardAnchor, setKeyboardAnchor] = useState<LocalPoint | null>(null);
  const [canvasFocused, setCanvasFocused] = useState(false);
  const [announcement, setAnnouncement] = useState(
    "键盘光标位于 UV 0 列、0 行",
  );
  const [panMode, setPanMode] = useState(false);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [stageSize, setStageSize] = useState({ width: 320, height: 320 });
  const [colorText, setColorText] = useState<string>(color);

  const viewSize = useMemo(
    () =>
      editorMode === "uv"
        ? { width: document.size, height: document.size }
        : getSurfaceSize(selectedSurface, document.model, document.size),
    [document.model, document.size, editorMode, selectedSurface],
  );
  const cellSize = editorMode === "uv" ? 512 / document.size : 18;

  useEffect(() => setColorText(color), [color]);

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;
    const observer = new ResizeObserver(([entry]) => {
      if (entry)
        setStageSize({
          width: entry.contentRect.width,
          height: entry.contentRect.height,
        });
    });
    observer.observe(stage);
    return () => observer.disconnect();
  }, []);

  const localToUv = useCallback(
    (point: LocalPoint) => {
      if (editorMode === "uv") return point;
      return surfaceToUv(
        selectedSurface,
        point.x,
        point.y,
        document.model,
        document.size,
      );
    },
    [document.model, document.size, editorMode, selectedSurface],
  );

  const addMirrorPoints = useCallback(
    (points: LocalPoint[]) => {
      if (!mirror) return points;
      if (editorMode !== "uv") {
        return [
          ...points,
          ...points.map((point) => ({
            x: viewSize.width - point.x - 1,
            y: point.y,
          })),
        ];
      }
      const mirrored: LocalPoint[] = [];
      for (const point of points) {
        const candidates = uvToSurface(
          point.x,
          point.y,
          document.model,
          activeLayer,
          document.size,
        );
        const candidate = candidates[0];
        if (!candidate) {
          mirrored.push({ x: document.size - 1 - point.x, y: point.y });
          continue;
        }
        const surface: SurfaceRef = {
          part: candidate.surface.part,
          layer: candidate.surface.layer,
          face: candidate.surface.face,
        };
        const size = getSurfaceSize(surface, document.model, document.size);
        mirrored.push(
          surfaceToUv(
            surface,
            size.width - candidate.x - 1,
            candidate.y,
            document.model,
            document.size,
          ),
        );
      }
      return [...points, ...mirrored];
    },
    [
      activeLayer,
      document.model,
      document.size,
      editorMode,
      mirror,
      viewSize.width,
    ],
  );

  const applyLocalPixels = useCallback(
    (points: LocalPoint[], nextColor: SkinColor) => {
      const expanded: LocalPoint[] = [];
      const radius = brushSize - 1;
      for (const point of points) {
        for (let offsetY = 0; offsetY <= radius; offsetY += 1) {
          for (let offsetX = 0; offsetX <= radius; offsetX += 1) {
            const x = point.x + offsetX - Math.floor(radius / 2);
            const y = point.y + offsetY - Math.floor(radius / 2);
            if (x >= 0 && y >= 0 && x < viewSize.width && y < viewSize.height)
              expanded.push({ x, y });
          }
        }
      }
      const unique = new Map<string, LocalPoint>();
      for (const point of addMirrorPoints(expanded))
        unique.set(`${point.x}:${point.y}`, point);
      const operations: SkinOperation[] = [];
      for (const point of unique.values()) {
        const uv = localToUv(point);
        operations.push({ type: "pixel", x: uv.x, y: uv.y, color: nextColor });
      }
      if (operations.length > 0) applyOperations(operations, "manual");
    },
    [
      addMirrorPoints,
      applyOperations,
      brushSize,
      localToUv,
      viewSize.height,
      viewSize.width,
    ],
  );

  const describePoint = useCallback(
    (point: LocalPoint) => {
      const uv = localToUv(point);
      return `UV ${uv.x} 列、${uv.y} 行`;
    },
    [localToUv],
  );

  useEffect(() => {
    setKeyboardCursor((current) => ({
      x: Math.min(current.x, viewSize.width - 1),
      y: Math.min(current.y, viewSize.height - 1),
    }));
    setKeyboardAnchor(null);
  }, [
    activeLayer,
    document.model,
    editorMode,
    selectedSurface.face,
    selectedSurface.part,
    tool,
    viewSize.height,
    viewSize.width,
  ]);

  useEffect(() => {
    if (isAiRunning) setKeyboardAnchor(null);
  }, [isAiRunning]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width = viewSize.width * cellSize;
    canvas.height = viewSize.height * cellSize;
    const context = canvas.getContext("2d");
    if (!context) return;
    context.imageSmoothingEnabled = false;
    context.clearRect(0, 0, canvas.width, canvas.height);

    for (let y = 0; y < viewSize.height; y += 1) {
      for (let x = 0; x < viewSize.width; x += 1) {
        context.fillStyle = checkerColor(x, y);
        context.fillRect(x * cellSize, y * cellSize, cellSize, cellSize);

        if (editorMode === "uv") {
          const value = colorAt(document.pixels, x, y, document.size);
          const owners = uvToSurface(
            x,
            y,
            document.model,
            undefined,
            document.size,
          );
          const visibleOwners = owners.filter(
            ({ surface }) =>
              (surface.layer === "inner" && showInner) ||
              (surface.layer === "outer" && showOuter),
          );
          if (owners.length === 0 || visibleOwners.length > 0) {
            const belongsToActiveLayer = visibleOwners.some(
              ({ surface }) => surface.layer === activeLayer,
            );
            context.fillStyle = colorToRgba(
              value,
              owners.length > 0 && !belongsToActiveLayer ? 0.28 : 1,
            );
            context.fillRect(x * cellSize, y * cellSize, cellSize, cellSize);
          }
        } else {
          const layers: SkinLayer[] = ["inner", "outer"];
          for (const layer of layers) {
            if (
              (layer === "inner" && !showInner) ||
              (layer === "outer" && !showOuter)
            )
              continue;
            const ref: SurfaceRef = { ...selectedSurface, layer };
            const uv = surfaceToUv(ref, x, y, document.model, document.size);
            const value = colorAt(document.pixels, uv.x, uv.y, document.size);
            context.fillStyle = colorToRgba(value);
            context.fillRect(x * cellSize, y * cellSize, cellSize, cellSize);
          }
        }
      }
    }

    context.beginPath();
    context.strokeStyle =
      editorMode === "uv" ? "rgba(44, 49, 37, .2)" : "rgba(44, 49, 37, .36)";
    context.lineWidth = 1;
    for (let x = 0; x <= viewSize.width; x += 1) {
      context.moveTo(x * cellSize + 0.5, 0);
      context.lineTo(x * cellSize + 0.5, canvas.height);
    }
    for (let y = 0; y <= viewSize.height; y += 1) {
      context.moveTo(0, y * cellSize + 0.5);
      context.lineTo(canvas.width, y * cellSize + 0.5);
    }
    context.stroke();

    if (editorMode === "uv") {
      context.font = "700 9px 'Pixelify Sans Variable', monospace";
      context.textBaseline = "top";
      for (const part of PARTS) {
        const surface: SurfaceRef = {
          part: part.value,
          layer: activeLayer,
          face: "front",
        };
        const size = getSurfaceSize(surface, document.model, document.size);
        const corners = [
          surfaceToUv(surface, 0, 0, document.model, document.size),
          surfaceToUv(
            surface,
            size.width - 1,
            size.height - 1,
            document.model,
            document.size,
          ),
        ];
        const left = Math.min(corners[0].x, corners[1].x) * cellSize;
        const top = Math.min(corners[0].y, corners[1].y) * cellSize;
        const width = (Math.abs(corners[0].x - corners[1].x) + 1) * cellSize;
        const height = (Math.abs(corners[0].y - corners[1].y) + 1) * cellSize;
        context.strokeStyle = activeLayer === "inner" ? "#e9784d" : "#53a9d7";
        context.lineWidth = 2;
        context.strokeRect(left + 1, top + 1, width - 2, height - 2);
        if (width > 32) {
          context.fillStyle = "rgba(255,253,246,.9)";
          context.fillRect(left + 2, top + 2, Math.min(width - 4, 42), 12);
          context.fillStyle = "#3d4639";
          context.fillText(part.label, left + 4, top + 3, width - 7);
        }
      }
    }

    if (canvasFocused) {
      if (keyboardAnchor) {
        context.strokeStyle = "#53a9d7";
        context.lineWidth = 3;
        context.strokeRect(
          keyboardAnchor.x * cellSize + 2,
          keyboardAnchor.y * cellSize + 2,
          cellSize - 4,
          cellSize - 4,
        );
      }
      context.strokeStyle = "#d74d25";
      context.lineWidth = 3;
      context.strokeRect(
        keyboardCursor.x * cellSize + 2,
        keyboardCursor.y * cellSize + 2,
        cellSize - 4,
        cellSize - 4,
      );
    }
  }, [
    activeLayer,
    canvasFocused,
    cellSize,
    document.model,
    document.pixels,
    document.revision,
    document.size,
    editorMode,
    keyboardAnchor,
    keyboardCursor,
    selectedSurface,
    showInner,
    showOuter,
    viewSize.height,
    viewSize.width,
  ]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target;
      if (
        target instanceof Element &&
        target.matches('input, textarea, select, [contenteditable="true"]')
      )
        return;
      const store = useSkinStore.getState();
      const command = event.metaKey || event.ctrlKey;
      if (command && event.key.toLowerCase() === "z") {
        event.preventDefault();
        if (event.shiftKey) store.redo();
        else store.undo();
        return;
      }
      if (command && event.key.toLowerCase() === "y") {
        event.preventDefault();
        store.redo();
        return;
      }
      const keyMap: Record<string, EditorTool> = {
        "1": "pencil",
        "2": "eraser",
        "3": "eyedropper",
        "4": "fill",
        l: "line",
        r: "rectangle",
      };
      const nextTool = keyMap[event.key.toLowerCase()];
      if (nextTool) store.setTool(nextTool);
      if (
        store.editorMode !== "part3d" &&
        (event.key === "+" || event.key === "=")
      )
        store.setZoom(Math.min(4, store.zoom + 0.25));
      if (store.editorMode !== "part3d" && event.key === "-")
        store.setZoom(Math.max(0.5, store.zoom - 0.25));
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const canvasPoint = (
    event: ReactPointerEvent<HTMLCanvasElement>,
  ): LocalPoint => {
    const bounds = event.currentTarget.getBoundingClientRect();
    return {
      x: Math.max(
        0,
        Math.min(
          viewSize.width - 1,
          Math.floor(
            ((event.clientX - bounds.left) / bounds.width) * viewSize.width,
          ),
        ),
      ),
      y: Math.max(
        0,
        Math.min(
          viewSize.height - 1,
          Math.floor(
            ((event.clientY - bounds.top) / bounds.height) * viewSize.height,
          ),
        ),
      ),
    };
  };

  const performImmediateTool = (point: LocalPoint) => {
    if (tool === "eyedropper") {
      const uv = localToUv(point);
      setColor(colorAt(document.pixels, uv.x, uv.y, document.size));
      return true;
    }
    if (tool === "fill") {
      const uv = localToUv(point);
      beginStroke();
      const target = colorAt(document.pixels, uv.x, uv.y, document.size);
      const visited = new Set<string>();
      const queue: LocalPoint[] = [point];
      const connected: LocalPoint[] = [];
      let cursor = 0;
      while (cursor < queue.length) {
        const current = queue[cursor++];
        const key = `${current.x}:${current.y}`;
        if (
          visited.has(key) ||
          current.x < 0 ||
          current.y < 0 ||
          current.x >= viewSize.width ||
          current.y >= viewSize.height
        ) {
          continue;
        }
        visited.add(key);
        const currentUv = localToUv(current);
        if (
          colorAt(document.pixels, currentUv.x, currentUv.y, document.size) !==
          target
        )
          continue;
        connected.push(current);
        queue.push(
          { x: current.x - 1, y: current.y },
          { x: current.x + 1, y: current.y },
          { x: current.x, y: current.y - 1 },
          { x: current.x, y: current.y + 1 },
        );
      }
      const unique = new Map<string, SkinOperation>();
      for (const local of addMirrorPoints(connected)) {
        const targetUv = localToUv(local);
        unique.set(`${targetUv.x}:${targetUv.y}`, {
          type: "pixel",
          x: targetUv.x,
          y: targetUv.y,
          color,
        });
      }
      if (unique.size > 0)
        applyOperations(Array.from(unique.values()), "manual");
      commitStroke();
      return true;
    }
    return false;
  };

  const onPointerDown = (event: ReactPointerEvent<HTMLCanvasElement>) => {
    if (
      (panMode && event.button === 0) ||
      event.button === 1 ||
      event.button === 2 ||
      event.shiftKey
    ) {
      event.preventDefault();
      panningRef.current = true;
      panStartRef.current = {
        pointerX: event.clientX,
        pointerY: event.clientY,
        panX: pan.x,
        panY: pan.y,
      };
      event.currentTarget.setPointerCapture(event.pointerId);
      return;
    }
    if (isAiRunning || event.button !== 0) return;
    const point = canvasPoint(event);
    setKeyboardAnchor(null);
    setCoordinate(point);
    if (performImmediateTool(point)) return;
    drawingRef.current = true;
    startRef.current = point;
    lastRef.current = point;
    beginStroke();
    event.currentTarget.setPointerCapture(event.pointerId);
    if (tool === "pencil" || tool === "eraser") {
      applyLocalPixels([point], tool === "eraser" ? "#00000000" : color);
    }
  };

  const onPointerMove = (event: ReactPointerEvent<HTMLCanvasElement>) => {
    if (panningRef.current) {
      setPan({
        x:
          panStartRef.current.panX +
          event.clientX -
          panStartRef.current.pointerX,
        y:
          panStartRef.current.panY +
          event.clientY -
          panStartRef.current.pointerY,
      });
      return;
    }
    const point = canvasPoint(event);
    setCoordinate(point);
    if (!drawingRef.current || (tool !== "pencil" && tool !== "eraser")) return;
    const last = lastRef.current ?? point;
    if (last.x === point.x && last.y === point.y) return;
    applyLocalPixels(
      bresenham(last, point),
      tool === "eraser" ? "#00000000" : color,
    );
    lastRef.current = point;
  };

  const finishPointer = (event: ReactPointerEvent<HTMLCanvasElement>) => {
    if (panningRef.current) {
      panningRef.current = false;
      if (event.currentTarget.hasPointerCapture(event.pointerId))
        event.currentTarget.releasePointerCapture(event.pointerId);
      return;
    }
    if (!drawingRef.current) return;
    const end = canvasPoint(event);
    const start = startRef.current ?? end;
    if (tool === "line") applyLocalPixels(bresenham(start, end), color);
    if (tool === "rectangle")
      applyLocalPixels(rectangleOutline(start, end, event.altKey), color);
    drawingRef.current = false;
    startRef.current = null;
    lastRef.current = null;
    commitStroke();
    if (event.currentTarget.hasPointerCapture(event.pointerId))
      event.currentTarget.releasePointerCapture(event.pointerId);
  };

  const onWheel = (event: ReactWheelEvent<HTMLCanvasElement>) => {
    event.preventDefault();
    setZoom(
      Math.max(0.5, Math.min(4, zoom + (event.deltaY < 0 ? 0.25 : -0.25))),
    );
  };

  const onCanvasKeyDown = (event: ReactKeyboardEvent<HTMLCanvasElement>) => {
    const directions: Record<string, LocalPoint> = {
      ArrowDown: { x: 0, y: 1 },
      ArrowLeft: { x: -1, y: 0 },
      ArrowRight: { x: 1, y: 0 },
      ArrowUp: { x: 0, y: -1 },
    };
    const direction = directions[event.key];
    if (direction) {
      event.preventDefault();
      const next = {
        x: Math.max(
          0,
          Math.min(viewSize.width - 1, keyboardCursor.x + direction.x),
        ),
        y: Math.max(
          0,
          Math.min(viewSize.height - 1, keyboardCursor.y + direction.y),
        ),
      };
      setKeyboardCursor(next);
      setCoordinate(next);
      setAnnouncement(`键盘光标移动到${describePoint(next)}`);
      return;
    }

    if (event.key !== " " && event.key !== "Enter") return;
    event.preventDefault();
    if (event.repeat) return;
    if (isAiRunning) {
      setAnnouncement("AI 正在落笔，键盘绘制已暂停");
      return;
    }

    const point = keyboardCursor;
    if (tool === "line" || tool === "rectangle") {
      if (!keyboardAnchor) {
        setKeyboardAnchor(point);
        setAnnouncement(
          `${tool === "line" ? "直线" : "矩形"}起点已设为${describePoint(point)}；移动光标后再次确认`,
        );
        return;
      }
      beginStroke();
      applyLocalPixels(
        tool === "line"
          ? bresenham(keyboardAnchor, point)
          : rectangleOutline(keyboardAnchor, point, event.altKey),
        color,
      );
      commitStroke();
      setAnnouncement(
        `已绘制${tool === "line" ? "直线" : "矩形"}，从${describePoint(keyboardAnchor)}到${describePoint(point)}`,
      );
      setKeyboardAnchor(null);
      return;
    }

    if (performImmediateTool(point)) {
      setAnnouncement(
        tool === "eyedropper"
          ? `已在${describePoint(point)}吸取颜色 ${colorAt(document.pixels, localToUv(point).x, localToUv(point).y, document.size)}`
          : `已在${describePoint(point)}填充颜色`,
      );
      return;
    }

    beginStroke();
    applyLocalPixels([point], tool === "eraser" ? "#00000000" : color);
    commitStroke();
    setAnnouncement(
      `${tool === "eraser" ? "已擦除" : "已绘制"}${describePoint(point)}`,
    );
  };

  const availableWidth = Math.max(80, stageSize.width - 42);
  const availableHeight = Math.max(80, stageSize.height - 42);
  const fitScale = Math.min(
    availableWidth / viewSize.width,
    availableHeight / viewSize.height,
  );
  const displayWidth = viewSize.width * fitScale;
  const displayHeight = viewSize.height * fitScale;
  const currentUv = coordinate ? localToUv(coordinate) : null;

  const updateSurface = (patch: Partial<SurfaceRef>) => {
    setSelectedSurface({ ...selectedSurface, ...patch });
  };

  const selectEditorMode = (mode: "part" | "part3d" | "uv") => {
    setPanMode(false);
    setEditorMode(mode);
  };

  const updateHex = (value: string) => {
    const normalized = value.startsWith("#") ? value : `#${value}`;
    setColorText(normalized);
    if (isSkinColor(normalized))
      setColor(normalized.toUpperCase() as SkinColor);
  };

  return (
    <section
      id="panel-draw"
      className={cx(
        panel.workspacePanel,
        styles.editorPanel,
        mobileActive && panel.mobileActive,
      )}
      aria-label="像素编辑器"
    >
      <header className={panel.panelHeading}>
        <div className={panel.panelTitleGroup}>
          <span className={panel.panelKicker}>PIXEL BENCH</span>
          <h2 className={panel.panelTitle}>像素工作台</h2>
        </div>
        <div className={panel.modeSwitch} aria-label="编辑视图">
          <button
            className={panel.segmentedButton}
            type="button"
            aria-pressed={editorMode === "part"}
            onClick={() => selectEditorMode("part")}
          >
            部位展开
          </button>
          <button
            className={panel.segmentedButton}
            type="button"
            aria-pressed={editorMode === "part3d"}
            onClick={() => selectEditorMode("part3d")}
          >
            部位 3D
          </button>
          <button
            className={panel.segmentedButton}
            type="button"
            aria-pressed={editorMode === "uv"}
            onClick={() => selectEditorMode("uv")}
          >
            {document.size}×{document.size} UV
          </button>
        </div>
      </header>

      <div className={styles.layerPopover} aria-label="图层可见性">
        {(["inner", "outer"] as const).map((layer) => (
          <div
            key={layer}
            className={cx(
              styles.layerToggle,
              activeLayer === layer && styles.activeLayer,
            )}
          >
            <input
              type="checkbox"
              aria-label={`显示${layer === "inner" ? "基础层" : "外套层"}`}
              checked={layer === "inner" ? showInner : showOuter}
              onChange={() => toggleLayer(layer)}
            />
            <button
              type="button"
              className={styles.undoAiButton}
              onClick={(event) => {
                event.preventDefault();
                setActiveLayer(layer);
              }}
            >
              {layer === "inner" ? "基础层" : "外套层"}
            </button>
          </div>
        ))}
      </div>

      <div className={styles.editorLayout}>
        <aside className={styles.toolRail} aria-label="绘制工具">
          {TOOLS.map((item) => (
            <button
              key={item.value}
              className={styles.toolButton}
              type="button"
              aria-label={`${item.label}（${item.key}）`}
              aria-pressed={tool === item.value}
              data-testid={`tool-${item.value}`}
              disabled={isAiRunning}
              onClick={() => setTool(item.value)}
              title={`${item.label} · ${item.key}`}
            >
              <Icon name={item.icon} />
            </button>
          ))}
          <span className={styles.toolDivider} />
          <button
            className={styles.toolButton}
            type="button"
            aria-label="水平镜像绘制"
            aria-pressed={mirror}
            disabled={isAiRunning}
            onClick={() => setMirror(!mirror)}
            title="镜像绘制"
          >
            <Icon name="mirror" />
          </button>
          <button
            className={styles.toolButton}
            type="button"
            aria-label={
              editorMode === "part3d" ? "旋转 3D 部位" : "抓手平移画布"
            }
            aria-pressed={panMode}
            data-testid="pan-toggle"
            onClick={() => setPanMode((active) => !active)}
            title={
              editorMode === "part3d"
                ? "观察模式 / 单指旋转"
                : "抓手 / 单指平移"
            }
          >
            <Icon name={editorMode === "part3d" ? "rotate" : "move"} />
          </button>
          <span className={styles.toolDivider} />
          <button
            className={styles.toolButton}
            type="button"
            aria-label="撤销"
            disabled={isAiRunning || history.length === 0}
            onClick={undo}
          >
            <Icon name="undo" />
          </button>
          <button
            className={styles.toolButton}
            type="button"
            aria-label="重做"
            disabled={isAiRunning || future.length === 0}
            onClick={redo}
          >
            <Icon name="redo" />
          </button>
        </aside>

        <div className={styles.editorMain}>
          {editorMode !== "uv" && (
            <nav className={styles.surfaceNav} aria-label="皮肤部位与朝向">
              <select
                className={styles.pixelSelect}
                aria-label="选择身体部位"
                value={selectedSurface.part}
                onChange={(event) =>
                  updateSurface({ part: event.target.value as SkinPart })
                }
              >
                {PARTS.map((part) => (
                  <option key={part.value} value={part.value}>
                    {part.label}
                  </option>
                ))}
              </select>
              {editorMode === "part" ? (
                <div className={styles.faceTabs}>
                  {FACES.map((face) => (
                    <button
                      key={face.value}
                      className={styles.faceButton}
                      type="button"
                      aria-pressed={selectedSurface.face === face.value}
                      onClick={() => updateSurface({ face: face.value })}
                    >
                      {face.label}
                    </button>
                  ))}
                </div>
              ) : (
                <span className={styles.part3DNavHint}>
                  在模型表面落笔即可切换当前面
                </span>
              )}
            </nav>
          )}

          <div
            ref={stageRef}
            className={cx(
              styles.canvasStage,
              editorMode === "part3d" && styles.part3DStage,
            )}
          >
            <span className={styles.canvasBadge} data-testid="canvas-badge">
              {editorMode === "uv"
                ? `${activeLayer === "inner" ? "BASE" : "OVERLAY"} · ${document.size}×${document.size}`
                : editorMode === "part3d"
                  ? `${surfaceLabel(selectedSurface.part)} · ${activeLayer === "inner" ? "基础层" : "外套层"} · ${FACES.find((face) => face.value === selectedSurface.face)?.label}`
                  : `${surfaceLabel(selectedSurface.part)} / ${FACES.find((face) => face.value === selectedSurface.face)?.label}`}
            </span>
            {editorMode === "part3d" ? (
              <Suspense
                fallback={
                  <div className={styles.part3DLoading} role="status">
                    正在装配部位 3D 画布…
                  </div>
                }
              >
                <Part3DEditor
                  onOrbitModeChange={setPanMode}
                  orbitMode={panMode}
                />
              </Suspense>
            ) : (
              <>
                <div
                  className={styles.canvasViewportControls}
                  aria-label="画布缩放"
                >
                  <button
                    className={styles.canvasControlButton}
                    type="button"
                    aria-label="缩小画布"
                    disabled={zoom <= 0.5}
                    onClick={() => setZoom(Math.max(0.5, zoom - 0.25))}
                  >
                    <Icon name="zoomOut" size={15} />
                  </button>
                  <button
                    className={cx(
                      styles.canvasControlButton,
                      styles.canvasResetButton,
                    )}
                    type="button"
                    aria-label="复位画布缩放和平移"
                    onClick={() => {
                      setZoom(1);
                      setPan({ x: 0, y: 0 });
                    }}
                  >
                    {Math.round(zoom * 100)}%
                  </button>
                  <button
                    className={styles.canvasControlButton}
                    type="button"
                    aria-label="放大画布"
                    disabled={zoom >= 4}
                    onClick={() => setZoom(Math.min(4, zoom + 0.25))}
                  >
                    <Icon name="zoomIn" size={15} />
                  </button>
                </div>
                <p id="skin-canvas-instructions" className="sr-only">
                  使用方向键移动像素光标，空格键或回车键使用当前工具。直线和矩形工具需确认起点与终点两次。
                </p>
                <p
                  id="skin-canvas-status"
                  className="sr-only"
                  aria-live="polite"
                  aria-atomic="true"
                >
                  {announcement}
                </p>
                <canvas
                  ref={canvasRef}
                  className={styles.skinCanvas}
                  data-testid="skin-editor-canvas"
                  data-pan-mode={panMode ? "true" : "false"}
                  data-tool={tool}
                  tabIndex={0}
                  aria-disabled={isAiRunning}
                  aria-describedby="skin-canvas-instructions skin-canvas-status"
                  aria-keyshortcuts="ArrowUp ArrowDown ArrowLeft ArrowRight Space Enter"
                  aria-label={
                    editorMode === "uv"
                      ? `${document.size}×${document.size} 皮肤 UV 像素画布`
                      : `${surfaceLabel(selectedSurface.part)}像素画布`
                  }
                  onContextMenu={(event) => event.preventDefault()}
                  onFocus={() => {
                    setCanvasFocused(true);
                    setCoordinate(keyboardCursor);
                    setAnnouncement(
                      `键盘光标位于${describePoint(keyboardCursor)}`,
                    );
                  }}
                  onBlur={() => {
                    setCanvasFocused(false);
                    setCoordinate(null);
                  }}
                  onKeyDown={onCanvasKeyDown}
                  onPointerDown={onPointerDown}
                  onPointerMove={onPointerMove}
                  onPointerUp={finishPointer}
                  onPointerCancel={finishPointer}
                  onPointerLeave={() => setCoordinate(null)}
                  onWheel={onWheel}
                  style={{
                    width: displayWidth,
                    height: displayHeight,
                    transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                  }}
                />
                <span className={styles.coordinateReadout}>
                  {currentUv
                    ? `UV ${String(currentUv.x).padStart(2, "0")}:${String(currentUv.y).padStart(2, "0")}`
                    : panMode
                      ? "抓手已开启，拖动可平移"
                      : "按住 Shift 拖动可平移"}
                </span>
              </>
            )}
            {isAiRunning && (
              <div className={styles.aiLockNotice}>
                <Icon name="sparkles" />
                AI 正在落笔，手工绘制已暂停
              </div>
            )}
          </div>

          <footer className={styles.editorFooter} data-testid="editor-footer">
            <div className={styles.colorControls}>
              <label
                className={styles.colorSwatchWrap}
                style={
                  {
                    "--current-color": color.slice(0, 7),
                  } as React.CSSProperties
                }
              >
                <span className="sr-only">选择颜色</span>
                <input
                  className={styles.colorInput}
                  type="color"
                  value={color.slice(0, 7)}
                  onChange={(event) =>
                    setColor(
                      `${event.target.value}${color.slice(7, 9)}`.toUpperCase() as SkinColor,
                    )
                  }
                />
              </label>
              <input
                className={styles.colorTextInput}
                aria-label="RGBA 十六进制颜色"
                value={colorText}
                maxLength={9}
                spellCheck={false}
                onChange={(event) => updateHex(event.target.value)}
                onBlur={() => setColorText(color)}
              />
              <label className={styles.opacityControl}>
                <span>不透明度</span>
                <span>
                  {Math.round(
                    (Number.parseInt(color.slice(7, 9), 16) / 255) * 100,
                  )}
                  %
                </span>
                <input
                  type="range"
                  min="0"
                  max="255"
                  value={Number.parseInt(color.slice(7, 9), 16)}
                  onChange={(event) =>
                    setColor(withAlpha(color, Number(event.target.value)))
                  }
                  style={{ gridColumn: "1 / -1" }}
                />
              </label>
              <div className={styles.recentColors} aria-label="最近使用的颜色">
                {recentColors.slice(0, 6).map((recent) => (
                  <button
                    key={recent}
                    className={styles.recentColor}
                    type="button"
                    aria-label={`使用颜色 ${recent}`}
                    style={
                      { "--swatch": colorToRgba(recent) } as React.CSSProperties
                    }
                    onClick={() => setColor(recent)}
                  />
                ))}
              </div>
            </div>
            <div
              className={styles.brushControls}
              aria-label="笔刷大小"
              role="group"
            >
              <span className={styles.brushLabel}>笔刷</span>
              <div className={styles.brushOptions}>
                {([1, 2, 4] as const).map((size) => (
                  <button
                    key={size}
                    className={styles.brushOption}
                    type="button"
                    aria-label={`${size} 像素笔刷`}
                    aria-pressed={brushSize === size}
                    onClick={() => setBrushSize(size)}
                  >
                    {size}px
                  </button>
                ))}
              </div>
            </div>
          </footer>
        </div>
      </div>
    </section>
  );
}
